import React, {PureComponent, useState} from 'react';
import styled from 'styled-components';
import {SortableContainer, SortableElement, SortableHandle, arrayMove} from 'react-sortable-hoc';

import Input from '../../components/Input';
import Button from '../../components/Button';
import Icon from '../../components/Icon';

import {createQuestionOption} from './shared';
import SubConfiguration from './SubConfiguration';

class OptionsBuilder extends PureComponent {
  state = {
    shouldFocusLastInput: false
  };

  componentDidUpdate(prevProps) {
    const {question: {questionOptions}} = this.props;

    if (prevProps.question.questionOptions.length < questionOptions.length) {
      this.setState({shouldFocusLastInput: true});
    }
  }

  setOptionsState = (reducer, onComplete) => {
    const {question, onChange} = this.props;
    onChange(typeof reducer === 'function' ? reducer(question.questionOptions) : reducer, onComplete);
  }

  handleOptionValueChange = (option, nextValue) => {
    const {question, onChangeDefaultAnswers} = this.props;

    if (option.value !== nextValue) {
      const [nextOptions, nextDefaultAnswers] = question.questionOptions.reduce((result, o) => {
        if (o === option) {
          result[0].push({...option, value: nextValue});

          if (question.defaultAnswers.indexOf(option.value) >= 0) {
            result[1] = [...result[1].filter(answer => answer !== option.value), nextValue];
          }
        }
        else {
          result[0].push(o);
        }

        return result;
      }, [[], question.defaultAnswers]);

      this.setOptionsState(nextOptions, () => onChangeDefaultAnswers(nextDefaultAnswers));
    }
  }

  handleRemoveOption = option => {
    this.setOptionsState(options => options.filter(o => o !== option));
  }

  handleAddOption = () => {
    this.setOptionsState(options => [...options, createQuestionOption('', options.length)]);
  }

  handleDefaultAnswersChange = (option, e) => {
    const {question, onChangeDefaultAnswers} = this.props;
    let nextAnswers;

    if (question.type === 'RADIO') {
      nextAnswers = e.target.checked ? [option.value] : [];
    }
    else {
      nextAnswers = question.defaultAnswers.filter(answer => answer !== option.value)
        .concat(e.target.checked ? [option.value] : []);
    }

    onChangeDefaultAnswers(nextAnswers);
  }

  handleSortOptions = ({oldIndex, newIndex}) => {
    const {question: {questionOptions}} = this.props;
    const nextOptions = arrayMove(questionOptions, oldIndex, newIndex)
      .map((option, i) => ({...option, order: i}));

    this.setOptionsState(nextOptions);
  }

  render() {
    const {question, validations, receivesSharedAnswers, noDefaultAnswers, optionPlaceholder} = this.props;
    const {shouldFocusLastInput} = this.state;

    return (
      <>
        <Options
          question={question}
          validations={validations}
          receivesSharedAnswers={receivesSharedAnswers}
          noDefaultAnswers={noDefaultAnswers}
          optionPlaceholder={optionPlaceholder}
          onChange={this.handleOptionValueChange}
          onRemove={this.handleRemoveOption}
          onChangeDefaultAnswers={this.handleDefaultAnswersChange}
          autoFocus={shouldFocusLastInput}
          onSortEnd={this.handleSortOptions}
          useWindowAsScrollContainer={true}
          useDragHandle={true}
          lockAxis="y"
        />
        <Button tertiary onClick={this.handleAddOption}>
          <Icon name="plus" /> Add an Option
        </Button>
      </>
    );
  }
}

// A question that receives shared answers has no default answer, and its own options are optional. An inventory pick
// list (noDefaultAnswers) never has a default answer either.
const Options = SortableContainer(({question, validations, receivesSharedAnswers, noDefaultAnswers, optionPlaceholder, onChange, onRemove, onChangeDefaultAnswers, autoFocus}) => (
  <SubConfiguration>
    {question.questionOptions.sort((a, b) => a.order - b.order).map((option, i) => (
      <Option
        key={`option_${option.value}_${i}`}
        index={i}
        question={question}
        option={option}
        validations={validations[option.value]}
        receivesSharedAnswers={receivesSharedAnswers}
        noDefaultAnswers={noDefaultAnswers}
        optionPlaceholder={optionPlaceholder}
        onChange={onChange}
        onRemove={onRemove}
        onChangeDefaultAnswers={onChangeDefaultAnswers}
        autoFocus={i === question.questionOptions.length - 1 && autoFocus}
      />
    ))}
  </SubConfiguration>
));

const Option = SortableElement(({question, option, validations, receivesSharedAnswers, noDefaultAnswers, optionPlaceholder, onChange, onRemove, onChangeDefaultAnswers, autoFocus}) => {
  const [value, setValue] = useState(option.value);
  return (
    <SubConfiguration.Item>
      <OptionContainer>
        <DragIcon />
        <OptionInputContainer>
          <Input
            type="text"
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder={optionPlaceholder || `Enter a ${question.type === 'RADIO' ? 'single-select' : 'multi-select'} option`}
            invalid={validations}
            validationText={validations}
            onBlur={e => {
              setValue(e.target.value.trim())
              onChange(option, e.target.value.trim())
            }}
            autoFocus={autoFocus}
          />
        </OptionInputContainer>
        <Button
          tertiary
          disabled={question.questionOptions.length === 1 && !receivesSharedAnswers}
          onClick={() => onRemove(option)}>
          <Icon name="trash-2" />
        </Button>
      </OptionContainer>
      {!question.required && !receivesSharedAnswers && !noDefaultAnswers && question.type === 'RADIO' && (
        <DefaultAnswerLabel>
          <input
            type="checkbox"
            checked={question.defaultAnswers[0] === option.value}
            onChange={e => onChangeDefaultAnswers(option, e)}
          />
          &nbsp;
          Set as the default answer
        </DefaultAnswerLabel>
      )}
      {!question.required && !noDefaultAnswers && question.type === 'CHECKBOXES' && (
        <DefaultAnswerLabel>
          <input
            type="checkbox"
            checked={question.defaultAnswers.indexOf(option.value) >= 0}
            onChange={e => onChangeDefaultAnswers(option, e)}
          />
          &nbsp;
          Add as a default answer
        </DefaultAnswerLabel>
      )}
    </SubConfiguration.Item>
  )
});

const OptionContainer = styled.div`
  display: flex;
  align-items: start;
`;

const DragIcon = SortableHandle(styled(props => <Icon name="align-justify" size="1.5em" color="#b4b4b4" {...props} />)`
  margin-top: 0.4rem;
  cursor: grab;
`);

const OptionInputContainer = styled.div`
  flex: 1;
  padding: 0 1rem;
`;

const DefaultAnswerLabel = styled.label`
  display: block;
  margin: 0.625rem 0 0 2.5rem;
`;

export default OptionsBuilder;